import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable, inject } from '@angular/core';
import type { DownloadTokenResultDto, GetByIdsInputDto } from '../dtos/shared/models';
import type { GetStandAreaMaintenancesInputDto, StandAreaMaintenanceCreateDto, StandAreaMaintenanceDto, StandAreaMaintenanceExcelDownloadDto, StandAreaMaintenanceUpdateDto } from '../dtos/stand-area-maintenance/models';

@Injectable({
  providedIn: 'root',
})
export class StandAreaMaintenanceService {
  private restService = inject(RestService);
  apiName = 'Geographic';
  

  create = (input: StandAreaMaintenanceCreateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto>({
      method: 'POST',
      url: '/api/geographic/standAreaMaintenances',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createMany = (input: StandAreaMaintenanceCreateDto[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, number>({
      method: 'POST',
      url: '/api/geographic/standAreaMaintenances/many',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/geographic/standAreaMaintenances/${id}`,
    },
    { apiName: this.apiName,...config });
  

  deleteByIds = (ids: number[], config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: '/api/geographic/standAreaMaintenances',
      params: { ids },
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto>({
      method: 'GET',
      url: `/api/geographic/standAreaMaintenances/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getDownloadToken = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, DownloadTokenResultDto>({
      method: 'GET',
      url: '/api/geographic/standAreaMaintenances/download-token',
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetStandAreaMaintenancesInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<StandAreaMaintenanceDto>>({
      method: 'GET',
      url: '/api/geographic/standAreaMaintenances',
      params: { periodId: input.periodId, adjustmentReasonId: input.adjustmentReasonId, rotationId: input.rotationId, cycleId: input.cycleId, rotationName: input.rotationName, currentArea: input.currentArea, currentAreaFrom: input.currentAreaFrom, currentAreaTo: input.currentAreaTo, gisArea: input.gisArea, gisAreaFrom: input.gisAreaFrom, gisAreaTo: input.gisAreaTo, areaToBeAdjusted: input.areaToBeAdjusted, areaToBeAdjustedFrom: input.areaToBeAdjustedFrom, areaToBeAdjustedTo: input.areaToBeAdjustedTo, differenceInPercentage: input.differenceInPercentage, differenceInPercentageFrom: input.differenceInPercentageFrom, differenceInPercentageTo: input.differenceInPercentageTo, isAreaUpdated: input.isAreaUpdated, notes: input.notes, standAreaMaintenanceTypeId: input.standAreaMaintenanceTypeId, standLocationId: input.standLocationId, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListAsExcelFile = (input: StandAreaMaintenanceExcelDownloadDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'GET',
      responseType: 'blob',
      url: '/api/geographic/standAreaMaintenances/as-excel-file',
      params: { downloadToken: input.downloadToken, fields: input.fields, periodId: input.periodId, adjustmentReasonId: input.adjustmentReasonId, rotationId: input.rotationId, cycleId: input.cycleId, rotationName: input.rotationName, currentArea: input.currentArea, currentAreaFrom: input.currentAreaFrom, currentAreaTo: input.currentAreaTo, gisArea: input.gisArea, gisAreaFrom: input.gisAreaFrom, gisAreaTo: input.gisAreaTo, areaToBeAdjusted: input.areaToBeAdjusted, areaToBeAdjustedFrom: input.areaToBeAdjustedFrom, areaToBeAdjustedTo: input.areaToBeAdjustedTo, differenceInPercentage: input.differenceInPercentage, differenceInPercentageFrom: input.differenceInPercentageFrom, differenceInPercentageTo: input.differenceInPercentageTo, isAreaUpdated: input.isAreaUpdated, notes: input.notes, standAreaMaintenanceTypeId: input.standAreaMaintenanceTypeId, standLocationId: input.standLocationId, isCursorPaging: input.isCursorPaging, includeTotalCount: input.includeTotalCount, id: input.id, idFrom: input.idFrom, idTo: input.idTo, creationTime: input.creationTime, creationTimeFrom: input.creationTimeFrom, creationTimeTo: input.creationTimeTo, creatorId: input.creatorId, lastModificationTime: input.lastModificationTime, lastModificationTimeFrom: input.lastModificationTimeFrom, lastModificationTimeTo: input.lastModificationTimeTo, lastModifierId: input.lastModifierId, deletionTime: input.deletionTime, deletionTimeFrom: input.deletionTimeFrom, deletionTimeTo: input.deletionTimeTo, deleterId: input.deleterId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getListByIds = (input: GetByIdsInputDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto[]>({
      method: 'GET',
      url: '/api/geographic/standAreaMaintenances/by-ids',
      params: { ids: input.ids },
    },
    { apiName: this.apiName,...config });
  

  getPendingAreaUpdate = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto[]>({
      method: 'GET',
      url: '/api/geographic/standAreaMaintenances/pending-area-update',
    },
    { apiName: this.apiName,...config });
  

  undoAreaUpdate = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto>({
      method: 'PUT',
      url: `/api/geographic/standAreaMaintenances/${id}/undo-area-update`,
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: StandAreaMaintenanceUpdateDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, StandAreaMaintenanceDto>({
      method: 'PUT',
      url: `/api/geographic/standAreaMaintenances/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
}